import { buildChildrenByParent } from "../components/ChatbotFlow/chatbotTreeModel";

function slotStepName(slot) {
  const id = String(slot.identificador || "").trim();
  const nome = String(slot.nome || "").trim();
  if (id && nome && id.toLowerCase() !== nome.toLowerCase()) {
    return `${id} — ${nome}`;
  }
  return id || nome || `Passo #${slot.id}`;
}

function buildSlotPath(slotId, slotById) {
  const parts = [];
  let current = slotById.get(Number(slotId));
  const seen = new Set();

  while (current) {
    const nid = Number(current.id);
    if (seen.has(nid)) break;
    seen.add(nid);
    parts.unshift(slotStepName(current));
    const parentId = Number(current.id_pai) || 0;
    current = parentId > 0 ? slotById.get(parentId) : null;
  }

  return parts.join(" › ");
}

function countDuplicateNomes(slots = []) {
  const counts = new Map();
  for (const slot of slots) {
    const key = String(slot?.nome || "").trim().toLowerCase();
    if (!key) continue;
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  return counts;
}

function regularChildren(slotId, childrenByParent) {
  return (childrenByParent.get(String(slotId)) || []).filter(
    (child) => String(child.identificador || "").trim() !== "Exceção"
  );
}

/**
 * Menor rank = aparece primeiro no picker de "Ir para".
 * 0 filho sequencial direto, 1 filho com identificador, 2 descendente, 3 irmão, 10 demais.
 */
export function navigationTargetRank(currentSlotId, targetSlotId, slotById, childrenByParent) {
  const current = Number(currentSlotId) || 0;
  const target = Number(targetSlotId) || 0;
  if (!current || !target) return 100;
  if (target === current) return 50;

  const directChildren = regularChildren(current, childrenByParent);
  const directChild = directChildren.find((child) => Number(child.id) === target);
  if (directChild) {
    return String(directChild.identificador || "").trim() ? 1 : 0;
  }

  let walk = slotById.get(target);
  const seen = new Set();
  while (walk) {
    const parentId = Number(walk.id_pai) || 0;
    if (parentId === current) return 2;
    if (!parentId || seen.has(parentId)) break;
    seen.add(parentId);
    walk = slotById.get(parentId);
  }

  const currentSlot = slotById.get(current);
  const targetSlot = slotById.get(target);
  if (
    currentSlot &&
    targetSlot &&
    Number(currentSlot.id_pai) > 0 &&
    Number(currentSlot.id_pai) === Number(targetSlot.id_pai)
  ) {
    return 3;
  }

  return 10;
}

function buildNavigationShortLabel(slot, duplicateNomeCounts) {
  const stepName = slotStepName(slot);
  const nomeKey = String(slot?.nome || "").trim().toLowerCase();
  if (nomeKey && (duplicateNomeCounts.get(nomeKey) || 0) > 1) {
    return `#${slot.id} · ${stepName}`;
  }
  return stepName;
}

export { buildSlotPath, slotStepName };

/**
 * Enriquece ações de navegação (tipo_acao=2) com caminho no fluxo quando a API ainda não envia.
 * @param {object[]} actions
 * @param {object[]} slots
 * @param {number|null} currentSlotId passo em edição — prioriza destinos do ramo atual
 */
export function enrichChatbotNavigationActions(actions = [], slots = [], currentSlotId = null) {
  if (!Array.isArray(actions) || !actions.length) return actions;

  const slotList = Array.isArray(slots) ? slots : [];
  const slotById = new Map(slotList.map((s) => [Number(s.id), s]));
  const childrenByParent = buildChildrenByParent(slotList);
  const duplicateNomeCounts = countDuplicateNomes(slotList);
  const currentId = Number(currentSlotId) || 0;
  const needsEnrich = actions.some((a) => Number(a.tipo_acao) === 2 && !a.slotPath);

  return actions.map((action) => {
    if (Number(action.tipo_acao) !== 2) return action;

    const slot = slotById.get(Number(action.id_acao));
    const slotPath =
      action.slotPath || (slot ? buildSlotPath(slot.id, slotById) : "");
    const stepName = slot ? buildNavigationShortLabel(slot, duplicateNomeCounts) : action.shortLabel || "";
    const searchText = [
      stepName,
      slotPath,
      slot?.identificador,
      slot?.nome,
      String(action.id_acao || action.slotId || ""),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const navRank =
      currentId && slot
        ? navigationTargetRank(currentId, slot.id, slotById, childrenByParent)
        : action.navRank ?? 100;

    if (!needsEnrich && action.slotPath && action.navRank != null) {
      return {
        ...action,
        navRank,
        hasDuplicateNome:
          slot &&
          String(slot.nome || "").trim() &&
          (duplicateNomeCounts.get(String(slot.nome).trim().toLowerCase()) || 0) > 1,
      };
    }

    if (!slot) return { ...action, navRank };

    return {
      ...action,
      label: `Ir para: ${slotPath}`,
      shortLabel: stepName,
      slotPath,
      slotId: Number(slot.id),
      group: action.group || "navigation",
      searchText,
      navRank,
      hasDuplicateNome:
        String(slot.nome || "").trim() &&
        (duplicateNomeCounts.get(String(slot.nome).trim().toLowerCase()) || 0) > 1,
    };
  });
}

export function sortNavigationCatalogActions(actions = []) {
  return (actions || []).slice().sort((a, b) => {
    const aNav = Number(a.tipo_acao) === 2;
    const bNav = Number(b.tipo_acao) === 2;
    if (aNav && bNav) {
      const rankDiff = (a.navRank ?? 100) - (b.navRank ?? 100);
      if (rankDiff !== 0) return rankDiff;
      return String(a.slotPath || a.label || "").localeCompare(String(b.slotPath || b.label || ""), "pt-BR");
    }
    return 0;
  });
}

export function findDuplicateNomeSlot(nome, slotId, slots = []) {
  const key = String(nome || "").trim().toLowerCase();
  if (!key) return null;
  return (
    (slots || []).find(
      (s) => Number(s.id) !== Number(slotId) && String(s.nome || "").trim().toLowerCase() === key
    ) || null
  );
}
